"use client";
import { useEffect, useRef, useState } from "react";
import styles from "./index.module.css";

import BrandedFooter from "../footer/branded";
import Drawermenu from "./drawer-menu";
import StoreSideMenu from "./store-side-menu";
import StoreHeader from "./store-header";

import { useAppDispatch, useAppSelector } from "../slice/hooks";
import { getActiveCart } from "../slice/cart-slice";
import { useHistoryManager } from "../history/useHistoryManager";
import ChatReachOutBox from "./ChatReachOutBox";
import { setActiveCategory, setCategories } from "../slice/category-slice";
import {
  setAccessPoint,
  setAccessPointId,
  setAccessPoints,
  setOrganisation,
} from "../slice/accessOrg-slice";

function StoreBox({
  organisation,
  children,
  modal,
  showCart,
  accessPointId,
  handleLocationChange,
  storePath,
  categories,
  switchCategory,
  activeCategory,
  searchTerm,
  setSearchTerm,
  isPageMode = false,
  headerClassName = "",
  bodyClassName = "",
  showSearchBar = true,
  isHomePage = false,
  searchBarRef = null,
  hasTyped = false,
  setHasTyped,
  accessPoint,
  accessPoints,
  navLocation,
  isSwitching = false,
  showMarketDiv = false,
  inMainMarketplace = false,
}) {
  // UI state

  const [isSticky, setIsSticky] = useState(false); // tracks if header should be sticky
  const [showMenu, setShowMenu] = useState(false); // drawer menu toggle
  const [searchFocus, setSearchFocus] = useState(false); // expanded search on mobile

  const [pastHomeSearch, setPastHomeSearch] = useState(false); // track scroll past homepage search

  // Redux state

  const cart = useAppSelector(getActiveCart);

  // refs
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const searchInputRef = useRef(null);

  const { navigateTo } = useHistoryManager(storePath);
  const dispatch = useAppDispatch();

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 750);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // --- Cart item count ---
  function calculateTotalItems(cart) {
    cart = cart || {};
    let { paymentItem } = cart;
    paymentItem = paymentItem || [];
    return paymentItem.reduce((total, item) => total + item.unit, 0);
  }
  const cartCount = calculateTotalItems(cart);

  // --- Search URL builder ---
  const urlWithSearchQuery = (term) => {
    if (typeof window !== "undefined") {
      const editedTerm = term.trim().replace(/\s+/g, "-");
      const fullPath = `${storePath}?q=${editedTerm}`;
      navigateTo(fullPath, { override: true }, false);
    }
  };

  const storeBoxLocationChange = (e) => {
    handleLocationChange(e);
  };

  const handleLogoClick = () => {
    setSearchTerm("");
    if (showSearchBar)
      navigateTo("home", {
        urlData: {
          organisation: organisation,
          accessPointId: accessPointId,
          accessPoint: accessPoint,
          accessPoints: accessPoints,
        },
      });
    else setShowMenu(true);
  };

  const handleLoginClick = () => {
    if (navLocation === "marketplace") {
      const cleanStorePath = String(storePath || "").replace(/\/+$/, "");
      navigateTo(`${cleanStorePath}/auth`);
    } else {
      navigateTo("auth");
    }
  };
  const onLogout = () => {};

  const onSeeAllSearchResults = () => {
    navigateTo(
      "market-place",
      {
        urlData: {
          accessPoint: null,
          accessPoints: [],
          accessPointId: null,
          organisation: null,
        },
        onNavigate: (navigate) => {
          if (navigate) {
            dispatch(setAccessPointId(null));
            dispatch(setAccessPoint(null));
            dispatch(setAccessPoints([]));
            dispatch(setOrganisation(null));
          }
        },
      },
      false,
    );
  };

  // --- Auto-focus search input when expanded ---
  useEffect(() => {
    if (searchFocus && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchFocus]);

  // --- Track header height for spacer when sticky ---
  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, []);

  // --- Sticky header logic (scroll or body lock) ---
  useEffect(() => {
    const handleScrollOrLock = () => {
      const bodyFixed = document.body.style.position === "fixed";
      if (bodyFixed) {
        setIsSticky(true);
      } else {
        setIsSticky(window.scrollY > 0);
      }
    };

    handleScrollOrLock(); // run on mount

    window.addEventListener("scroll", handleScrollOrLock);

    // Also track body style changes (when search modal locks body)
    const observer = new MutationObserver(() => handleScrollOrLock());
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["style"],
    });

    return () => {
      window.removeEventListener("scroll", handleScrollOrLock);
      observer.disconnect();
    };
  }, []);

  // --- Handle search input changes ---
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm?.(value);
  };

  const cancelSearch = (e) => {
    e.preventDefault();
    setSearchTerm("");
    setSearchFocus(false);
    setHasTyped?.(false);
  };

  // --- Track if user scrolled past the home page search bar ---
  useEffect(() => {
    if (!isHomePage || !searchBarRef?.current) return;

    const handleScroll = () => {
      if (!searchBarRef?.current) return;
      const rect = searchBarRef.current.getBoundingClientRect();
      const scrolledPast = rect.bottom <= headerHeight; // compare position
      setPastHomeSearch(scrolledPast);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomePage, searchBarRef, headerHeight]);

  const [isAtTop, setIsAtTop] = useState(true);

  useEffect(() => {
    const onScroll = () => {
      setIsAtTop(window.scrollY === 0);
    };

    window.addEventListener("scroll", onScroll);
    onScroll(); // initial check

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Use debounced search to reduce flicker in UI
  const hasSearch = searchTerm !== "";

  return (
    <>
      <div className={`${styles.page} ${bodyClassName}`}>
        {navLocation === "marketplace" &&
          // && searchTerm === ""
          showMarketDiv && (
            <div
              style={{
                backgroundColor: "#0e86d4",
                color: "white",
                padding: "1rem",
                cursor: "pointer",
              }}
              onClick={() => {
                navigateTo(
                  "market-place/stores",
                  {
                    urlData: {
                      accessPoint: null,
                      accessPoints: [],
                      accessPointId: null,
                      organisation: null,
                    },
                    onNavigate: (navigate) => {
                      if (navigate) {
                        dispatch(setAccessPointId(null));
                        dispatch(setAccessPoint(null));
                        dispatch(setAccessPoints([]));
                        dispatch(setOrganisation(null));
                        dispatch(setActiveCategory(null));
                        dispatch(setCategories(null));
                      }
                    },
                  },
                  false,
                );
              }}
            >
              <h2>Go to our MarketPlace.</h2>
            </div>
          )}

        <StoreHeader
          headerRef={headerRef}
          headerClassName={headerClassName}
          showSearchBar={showSearchBar}
          isSticky={isSticky}
          isAtTop={isAtTop}
          searchFocus={searchFocus}
          storePath={storePath}
          hasSearch={hasSearch}
          handleLogoClick={handleLogoClick}
          isSwitching={isSwitching}
          organisation={organisation}
          accessPoints={accessPoints}
          accessPointId={accessPointId}
          hasTyped={hasTyped}
          isHomePage={isHomePage}
          pastHomeSearch={pastHomeSearch}
          searchTerm={searchTerm}
          searchInputRef={searchInputRef}
          handleSearch={handleSearch}
          cancelSearch={cancelSearch}
          setSearchFocus={setSearchFocus}
          setHasTyped={setHasTyped}
          urlWithSearchQuery={urlWithSearchQuery}
          navLocation={navLocation}
          onSeeAllSearchResults={onSeeAllSearchResults}
          modal={modal}
          storeBoxLocationChange={storeBoxLocationChange}
          isPageMode={isPageMode}
          showCart={showCart}
          cartCount={cartCount}
          onLogout={onLogout}
          handleLoginClick={handleLoginClick}
        />

        {/* Spacer to avoid content jump when header sticks */}
        {(showSearchBar || isSticky) && (
          <div style={{ height: headerHeight }} />
        )}

        {/* Overlay to close mobile search */}
        {searchFocus && !isHomePage && (
          <div
            className={styles.searchOverlay}
            onClick={() => setSearchFocus(false)}
          />
        )}

        {/* Page content */}
        {children}

        {/* Footer */}
        <div className={`${styles.footer} box-footer`}>
          <BrandedFooter
            organisation={organisation}
            storePath={storePath}
            isSwitching={isSwitching}
          />
        </div>
      </div>
      {storePath !== "market-place" && (
        <ChatReachOutBox
          modal={modal}
          organisation={organisation}
          accessPoint={accessPoint}
        />
      )}

      {/* Drawer menu (mobile only) */}
      {showMenu && !showSearchBar && isMobile ? (
        <Drawermenu setShowMenu={setShowMenu} showMenu={showMenu}>
          {({ closeMenu }) => (
            <StoreSideMenu
              organisation={organisation}
              accessPoints={accessPoints}
              accessPointId={accessPointId}
              categories={categories}
              activeCategory={activeCategory}
              onClose={closeMenu}
              onSwitchCategory={(e) => {
                closeMenu();
                switchCategory(e);
              }}
              onLocationChange={(e) => {
                storeBoxLocationChange(e);
                closeMenu();
              }}
            />
          )}
        </Drawermenu>
      ) : null}
    </>
  );
}

export default StoreBox;
